/**
 * GearGhost's vendor terms, to read and to accept (docs/plans/taxes.md,
 * Decisions 46 and 47; #1043 "Vendor marketplace-facilitator notice and
 * terms language").
 *
 * Nothing here is its own UI: the modal is checkout's read-and-accept modal
 * (common/legal/viewRentalAgreement), and the text is the shared
 * LegalDocument drawing `legal/vendor-terms`. This file only puts the one
 * inside the other, so the store's row on Account Standing and the
 * become-a-vendor Terms step open the same thing.
 *
 * `version` is the version in force, which accepting names. `onAccept` is
 * left out to read terms already accepted: the modal then shows no button.
 * "I Accept" is held until this version's text is on the page; a modal
 * opened again, or whose version changes while it is open, reads the text
 * again and holds the button again.
 *
 * `onShown`, when given, is called each time the text comes onto the page.
 * A store's row passes it to tell the server the terms were seen (Decision
 * 53); the become-a-vendor step has no store yet and passes none. It has to
 * be a stable function (useCallback): the document reads again when the
 * function it is handed changes.
 *
 * The document, with the Markdown renderer it draws through, loads the
 * first time the terms open: the setup banner puts this modal on every
 * dashboard page, and few openings of a page open the terms. Until it
 * arrives the modal's body is empty, then the document says Loading... as
 * it reads the text.
 */
import { lazy, Suspense, useCallback, useState } from 'react'
import { VENDOR_TERMS_DOCUMENT_SLUG } from '@gearghost/api-schemas/vendor'
import ReadAndAcceptModal from '@common/legal/viewRentalAgreement'
import { importWithRetry } from '@/navigation/utils/lazyRoute'

const LegalDocument = lazy(() => importWithRetry(() => import('@common/legal/legal-document')))

type Props = {
  show: boolean
  onHide: () => void
  version: number
  onAccept?: () => void
  onShown?: () => void
}

/** Mounted only while open, so each opening starts with the text not yet shown. */
const OpenVendorTermsModal = ({ onHide, onAccept, onShown }: Omit<Props, 'show' | 'version'>) => {
  const [shown, setShown] = useState(false)
  const markShown = useCallback(() => {
    setShown(true)
    if (onShown) onShown()
  }, [onShown])

  return (
    <ReadAndAcceptModal
      show
      onHide={onHide}
      heading='GearGhost Terms of Service'
      showAcceptButton={onAccept !== undefined}
      acceptDisabled={!shown}
      onAccept={onAccept}
    >
      <Suspense fallback={null}>
        <LegalDocument slug={VENDOR_TERMS_DOCUMENT_SLUG} onLoaded={markShown} />
      </Suspense>
    </ReadAndAcceptModal>
  )
}

/** Keyed by version: a new version in force is a new text, read from the start. */
const VendorTermsModal = ({ show, version, ...open }: Props) =>
  show ? <OpenVendorTermsModal key={version} {...open} /> : null

export default VendorTermsModal
